@if (mobileOpen()) {
  <div class="mobile-nav-backdrop" (click)="closeOverlay()"></div>

  <nav class="mobile-nav" aria-label="Main navigation">
    <div class="mobile-nav__scroll">
      @for (section of sections(); track section.id) {
        @if (section.hasEntries) {
          <div class="mobile-nav__section" [class.mobile-nav__section--expanded]="section.expanded">
            <button
              type="button"
              class="mobile-nav__section-header"
              [class.mobile-nav__section-header--active]="section.active"
              [attr.aria-expanded]="section.expanded"
              [attr.data-test]="section.dataTest"
              (click)="toggleSection(section)">
              <mat-icon class="mobile-nav__section-icon material-icons-outlined">{{ section.icon }}</mat-icon>
              <span class="mobile-nav__label">{{ section.translationKey | translate }}</span>
              @if (section.newTag) {
                <span class="mobile-nav__new-tag">NEW</span>
              }
              @if (section.showUpgradeTag) {
                <app-account-upgrade-tag></app-account-upgrade-tag>
              }
              @if (section.adminOnly) {
                <mat-icon class="mobile-nav__admin-tag">font_download</mat-icon>
              }
              <span class="mobile-nav__chevron-hit" (click)="toggleSectionChevron($event, section)">
                <mat-icon class="mobile-nav__chevron">chevron_right</mat-icon>
              </span>
            </button>

            @if (section.expanded) {
              <div class="mobile-nav__entries">
                @for (entry of section.entries; track $index) {
                  @switch (entry.kind) {
                    @case ("separator") {
                      <hr class="mobile-nav__separator" />
                    }
                    @case ("group") {
                      <button
                        type="button"
                        class="mobile-nav__group-toggle"
                        [class.mobile-nav__group-toggle--expanded]="entry.expanded"
                        [attr.aria-expanded]="entry.expanded"
                        [attr.data-test]="entry.dataTest"
                        (click)="toggleGroup(entry)">
                        <span class="mobile-nav__label">{{ entry.translationKey | translate }}</span>
                        @if (entry.adminOnly) {
                          <mat-icon class="mobile-nav__admin-tag">font_download</mat-icon>
                        }
                        <mat-icon class="mobile-nav__chevron">chevron_right</mat-icon>
                      </button>
                      @if (entry.expanded) {
                        @for (item of entry.items; track item.translationKey) {
                          <ng-container *ngTemplateOutlet="leaf; context: { $implicit: item, nested: true }"></ng-container>
                        }
                      }
                    }
                    @default {
                      <ng-container *ngTemplateOutlet="leaf; context: { $implicit: entry, nested: false }"></ng-container>
                    }
                  }
                }
              </div>
            }
          </div>
        } @else {
          <!-- A section with no sub-entries is a single direct link to its landing page
             (no toggle, no chevron, no duplicate label) — matches the desktop rail. -->
          <div class="mobile-nav__section">
            <a
              class="mobile-nav__section-header"
              [class.mobile-nav__section-header--active]="section.active"
              [routerLink]="section.url"
              [attr.data-test]="section.dataTest"
              (click)="onNavigate()">
              <mat-icon class="mobile-nav__section-icon material-icons-outlined">{{ section.icon }}</mat-icon>
              <span class="mobile-nav__label">{{ section.translationKey | translate }}</span>
              @if (section.newTag) {
                <span class="mobile-nav__new-tag">NEW</span>
              }
              @if (section.showUpgradeTag) {
                <app-account-upgrade-tag></app-account-upgrade-tag>
              }
              @if (section.adminOnly) {
                <mat-icon class="mobile-nav__admin-tag">font_download</mat-icon>
              }
            </a>
          </div>
        }
      }
    </div>

    @if (currentAccountName()) {
      <div class="mobile-nav__account" [class.mobile-nav__account--expanded]="accountSwitcherOpen()">
        <button
          type="button"
          class="mobile-nav__account-current"
          [disabled]="!hasMultipleAccounts"
          [attr.aria-expanded]="hasMultipleAccounts ? accountSwitcherOpen() : null"
          data-test="mobile-nav-account-switcher"
          (click)="toggleAccountSwitcher()">
          <mat-icon class="mobile-nav__account-icon material-icons-outlined">folder</mat-icon>
          <span class="mobile-nav__account-text">
            <span class="mobile-nav__account-label">{{ "mobile-nav.account.current" | translate }}</span>
            <span class="mobile-nav__account-name">{{ currentAccountName() }}</span>
          </span>
          @if (hasMultipleAccounts) {
            <mat-icon class="mobile-nav__chevron mobile-nav__account-chevron">expand_less</mat-icon>
          }
        </button>

        @if (accountSwitcherOpen()) {
          <div class="mobile-nav__account-list">
            @if (showAccountFilter()) {
              <input
                type="text"
                class="mobile-nav__account-filter"
                [placeholder]="'mobile-nav.account.search' | translate"
                [title]="'mobile-nav.account.search' | translate"
                [attr.aria-label]="'mobile-nav.account.search' | translate"
                [value]="''"
                (input)="onAccountFilter($any($event.target).value)" />
            }

            @if (accountsLoading()) {
              <div class="mobile-nav__account-status">{{ "mobile-nav.account.loading" | translate }}</div>
            } @else if (filteredAccounts().length === 0) {
              <div class="mobile-nav__account-status">{{ "mobile-nav.account.empty" | translate }}</div>
            } @else {
              @for (account of filteredAccounts(); track account.id) {
                <button type="button" class="mobile-nav__account-option" [disabled]="switchingAccount()" (click)="switchAccount(account.id)">
                  <span class="mobile-nav__label">{{ account.name }}</span>
                </button>
              }
            }
          </div>
        }
      </div>
    }
  </nav>

  <!-- A single leaf renderer reused at the top level and inside groups so every kind
       (link / external / action / disabled) behaves consistently wherever it appears. -->
  <ng-template #leaf let-item let-nested="nested">
    @switch (item.kind) {
      @case ("external") {
        <a
          class="mobile-nav__item"
          [class.mobile-nav__item--nested]="nested"
          [href]="item.externalUrl"
          target="_blank"
          rel="noopener noreferrer"
          [attr.data-test]="item.dataTest"
          (click)="onNavigate()">
          <span class="mobile-nav__label">{{ item.translationKey | translate }}</span>
          <mat-icon class="mobile-nav__external-icon">open_in_new</mat-icon>
        </a>
      }
      @case ("action") {
        <button
          type="button"
          class="mobile-nav__item"
          [class.mobile-nav__item--nested]="nested"
          [attr.data-test]="item.dataTest"
          (click)="onAction(item.actionType)">
          <span class="mobile-nav__label">{{ item.translationKey | translate }}</span>
          @if (item.adminOnly) {
            <mat-icon class="mobile-nav__admin-tag">font_download</mat-icon>
          }
        </button>
      }
      @case ("disabled") {
        <button
          type="button"
          class="mobile-nav__item mobile-nav__item--disabled"
          [class.mobile-nav__item--nested]="nested"
          disabled
          [attr.data-test]="item.dataTest">
          <span class="mobile-nav__label">{{ item.translationKey | translate }}</span>
          @if (item.showUpgradeTag) {
            <app-account-upgrade-tag></app-account-upgrade-tag>
          }
          @if (item.adminOnly) {
            <mat-icon class="mobile-nav__admin-tag">font_download</mat-icon>
          }
        </button>
      }
      @default {
        <a
          class="mobile-nav__item"
          [class.mobile-nav__item--nested]="nested"
          [routerLink]="item.url"
          [queryParams]="item.queryParams"
          routerLinkActive="mobile-nav__item--active"
          [routerLinkActiveOptions]="routerLinkActiveOptions"
          [attr.data-test]="item.dataTest"
          (click)="onNavigate()">
          <span class="mobile-nav__label">{{ item.translationKey | translate }}</span>
          @if (item.betaTag) {
            <span class="mobile-nav__beta-tag">BETA</span>
          }
          @if (item.showUpgradeTag) {
            <app-account-upgrade-tag></app-account-upgrade-tag>
          }
          @if (item.adminOnly) {
            <mat-icon class="mobile-nav__admin-tag">font_download</mat-icon>
          }
        </a>
        @if (item.showChildren) {
          @for (child of item.children; track child.translationKey) {
            <a
              class="mobile-nav__item mobile-nav__item--child"
              [class.mobile-nav__item--nested]="nested"
              [routerLink]="child.url"
              [queryParams]="child.queryParams"
              routerLinkActive="mobile-nav__item--active"
              [routerLinkActiveOptions]="routerLinkActiveOptions"
              [attr.data-test]="child.dataTest"
              (click)="onNavigate()">
              <span class="mobile-nav__label">{{ child.translationKey | translate }}</span>
            </a>
          }
        }
      }
    }
  </ng-template>
}
